import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { TreeDrillCacheService } from '../../shared-services/tree-drill-cache.service';

export interface MineHeaderInfo {
  name: string;
  details: { label: string; value: string }[];
}

export interface RiskFactor {
  label: string;
  score: number;
  isHeader?: boolean;
  expandable?: boolean;
  path?: string;
  hideSubArrows?: boolean;
  subFactors?: { label: string; score: number; path?: string; engineModelKey?: string; multiline?: boolean }[];
}

@Component({
  selector: 'app-tree-drill-down',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tree-drill-down.component.html',
  styleUrls: ['./tree-drill-down.component.scss'],
})
export class TreeDrillDownComponent implements OnInit, OnChanges {
  @Input() data: any;
  private readonly nullEngineFlowDebugKey = '[RP_NULL_ENGINE_FLOW_V2]';

  mineInfo: MineHeaderInfo = { name: '', details: [] };
  riskFactors: RiskFactor[] = [];
  mineNoteText = '';
  opportunityText = '';
  expandedFactors = new Set<string>();
  loading = true;
  private primaryEngineModelKey = '';
  private engineModelItems: any[] = [];
  private cachedMineKey = '';
  private cachedResponse: any = null;
  private isInitialized = false;
  private isCurrentMineInactive = false;

  catastrophicFeature = {
    label: 'Catastrophic Failure',
    isExpanded: false,
    score: 0,
    engineModels: [] as { raw: string, display: string }[]
  };

  constructor(private configService: ConfigService, private router: Router, private treeDrillCache: TreeDrillCacheService) { }

  ngOnChanges(changes: SimpleChanges): void {
    // Only handle mine changes if already initialized
    if (!this.isInitialized) {
      return;
    }

    if (changes['data']) {
      const currentSelectionKey = this.getCurrentSelectionKey();
      // Only refetch if the request-driving selection has changed
      if (currentSelectionKey && currentSelectionKey !== this.cachedMineKey) {
        this.cachedMineKey = currentSelectionKey;
        this.cachedResponse = null; // Invalidate cache for new mine
        this.loadTreeDrillData();
      }
    }
  }

  ngOnInit(): void {
    // Pre-fill mine name from config so it shows immediately
    this.mineInfo.name =
      this.getValueByPath(this.data, 'customPayload.mine') || '';

    // Restore expanded factors from previous navigation state
    const previousNavState = (window?.history?.state || {}) as any;
    this.isCurrentMineInactive = previousNavState?.selectedMine?.__isInactive === true;
    if (previousNavState.expandedFactors && Array.isArray(previousNavState.expandedFactors)) {
      this.expandedFactors = new Set(previousNavState.expandedFactors);
    }
    if (previousNavState.catastrophicExpanded) {
      this.catastrophicFeature.isExpanded = true;
    }

    // Pre-fill risk factor labels from keyMappings (scores default to 0)
    this.initRiskLabels();

    // Load data on first init - ONLY from ngOnInit
    const currentSelectionKey = this.getCurrentSelectionKey();
    if (currentSelectionKey) {
      this.cachedMineKey = currentSelectionKey;
      this.loadTreeDrillData();
    }

    this.isInitialized = true;
  }

  private loadTreeDrillData(): void {
    if (!this.data?.apiUrl) {
      this.loading = false;
      return;
    }

    const currentMine = this.data?.customPayload?.mine || '';
    const activeFlag = this.getCurrentActiveFlag();
    const currentSelectionKey = this.getCurrentSelectionKey();
    const apiUrl = this.data.apiUrl;

    // Check global service cache first
    const cachedData = this.treeDrillCache.get(currentMine, apiUrl, activeFlag);
    if (cachedData) {
      this.cachedResponse = cachedData;
      this.cachedMineKey = currentSelectionKey;
      this.parseResponse(cachedData);
      this.loading = false;
      return;
    }

    // Check local component cache
    if (this.cachedResponse && this.cachedMineKey === currentSelectionKey) {
      this.parseResponse(this.cachedResponse);
      this.loading = false;
      return;
    }

    // Make API call only if not in any cache
    this.configService
      .getConfig(apiUrl, this.data, {}, this.data.method || 'post')
      .subscribe({
        next: (res: any) => {
          this.cachedResponse = res;
          this.cachedMineKey = currentSelectionKey;
          // Store in global cache for other screens
          this.treeDrillCache.set(currentMine, apiUrl, res, activeFlag);
          this.parseResponse(res);
          this.loading = false;
        },
        error: () => (this.loading = false),
      });
  }

  private getCurrentActiveFlag(): string {
    return `${this.data?.customPayload?.active || ''}`.trim().toLowerCase();
  }

  private getCurrentSelectionKey(): string {
    const mine = `${this.data?.customPayload?.mine || ''}`.trim();
    const activeFlag = this.getCurrentActiveFlag();
    return [mine, activeFlag].filter(Boolean).join('|');
  }

  onFactorClick(rf: RiskFactor): void {
    if (!this.isFactorClickable(rf)) {
      return;
    }

    const isActiveRepowers = `${rf?.label || ''}`.trim().toLowerCase().startsWith('active repowers');
    const hasNullEngineSubFactor = isActiveRepowers && Array.isArray(rf?.subFactors)
      ? rf.subFactors.some((sub: any) => !`${sub?.engineModelKey ?? ''}`.trim())
      : false;
    const expandable = this.canExpandFactor(rf);
    const wasExpanded = this.isExpanded(rf.label);

    if (hasNullEngineSubFactor) {
      console.log(this.nullEngineFlowDebugKey, 'onFactorClick.before', {
        label: rf.label,
        mine: this.mineInfo?.name || this.getValueByPath(this.data, 'customPayload.mine') || '',
        expandable,
        wasExpanded,
        subFactors: rf.subFactors
      });
    }

    if (expandable) {
      this.toggleFactor(rf.label);
      if (hasNullEngineSubFactor) {
        console.log(this.nullEngineFlowDebugKey, 'onFactorClick.after', {
          label: rf.label,
          isExpandedNow: this.isExpanded(rf.label)
        });
      }
      return;
    }

    if (rf.path) {
      const ownerValue = this.mineInfo.details.find((detail) => detail.label === 'Owner')?.value || '';
      const engineValue = this.mineInfo.details.find((detail) => detail.label === 'Engine')?.value || '';
      const fallbackEngine = engineValue.split('/')[0]?.trim() || engineValue;
      const mineSite = this.mineInfo.name || '';
      const previousState = (window?.history?.state || {}) as any;
      const engineContext = this.resolveEngineContextForNavigation(
        previousState,
        this.primaryEngineModelKey || fallbackEngine
      );

      this.navigateToPath(
        rf.path,
        this.buildNavigationState(
          mineSite,
          engineContext.engineModelKey,
          engineContext.selectedEngineLabel,
          ownerValue,
          previousState
        )
      );
    }
  }

  /* ---------- response parsing -------------------------------------- */
  private parseResponse(res: any): void {
    const km = this.data?.keyMappings;
    if (!km || !res) return;
    this.isCurrentMineInactive = this.isCurrentMineInactive || this.detectCurrentMineInactive(res);

    // Navigate to the data object inside the response
    const dataObj = km.responsePath
      ? this.getValueByPath(res, km.responsePath)
      : res;

    if (!dataObj) return;

    // Header details (Owner, Engine, Fleet …)
    const headerKeys: Record<string, string> = km.headerKeys || {};
    this.engineModelItems = Array.isArray(dataObj?.engine_model) ? dataObj.engine_model : [];
    this.primaryEngineModelKey = this.extractPrimaryEngineModelKey(dataObj);
    this.mineInfo.details = Object.keys(headerKeys).map((label) => ({
      label,
      value: this.resolveHeaderValue(label, headerKeys[label], dataObj),
    }));

    // Optional mine-specific note shown below header details.
    this.mineNoteText = this.resolveMineNoteText(km, dataObj);

    // Populate Catastrophic Failure Engine Models
    const catKeys = this.getEngineModelKeys(dataObj);
    const uniqueMap = new Map<string, string>();
    catKeys.forEach(k => {
      const display = k.split(',')[0]?.trim() || k;
      if (!uniqueMap.has(display)) {
        uniqueMap.set(display, k);
      }
    });

    this.catastrophicFeature.engineModels = Array.from(uniqueMap.entries()).map(([display, raw]) => {
      return { raw, display };
    });

    // Set catastrophic score from the response
    const catastrophicScore = this.getValueByPath(dataObj, 'catastrophicScore') 
      || this.getValueByPath(dataObj, 'catastrophic_score');
    this.catastrophicFeature.score = Number.isFinite(Number(catastrophicScore)) ? Number(catastrophicScore) : 0;

    // Risk factor scores
    const rfConfigs: any[] = km.riskFactors || [];
    const explicitActiveRepowerFlag = this.getExplicitActiveRepowerFlag();
    this.riskFactors = rfConfigs.map((rf: any) => {
      const baseLabel = `${rf?.label || ''}`.trim();
      const normalizedLabel = baseLabel.toLowerCase();
      const isActiveRepowersFactor = normalizedLabel === 'active repowers';
      const subFactors = this.buildSubFactors(dataObj, rf);

      let label = baseLabel;
      let expandable = !!rf.expandable;

      if (isActiveRepowersFactor) {
        const hasValidActiveRepowerData = this.hasValidActiveRepowerData(dataObj);
        const isActiveMine = explicitActiveRepowerFlag === true ||
          (explicitActiveRepowerFlag === null && hasValidActiveRepowerData);
        const shouldEnableActiveRepowers = isActiveMine && hasValidActiveRepowerData;
        const repowerRows = Array.isArray(dataObj?.repower_data) ? dataObj.repower_data : [];
        const hasNullEngineRepower = repowerRows.some((row: any) => !`${row?.engine_model ?? row?.engine_model_key ?? ''}`.trim());

        label = `${baseLabel} - ${isActiveMine ? 'Yes' : 'No'}`;
        expandable = shouldEnableActiveRepowers && !!rf.expandable;

        if (hasNullEngineRepower) {
          console.log(this.nullEngineFlowDebugKey, 'parseResponse.activeRepowers', {
            mine: this.mineInfo?.name || this.getValueByPath(this.data, 'customPayload.mine') || '',
            activeFlag: this.getCurrentActiveFlag(),
            explicitActiveRepowerFlag,
            hasValidActiveRepowerData,
            isActiveMine,
            shouldEnableActiveRepowers,
            expandable,
            subFactorCount: Array.isArray(subFactors) ? subFactors.length : 0,
            repowerRows
          });
        }
      }

      return {
        label,
        score: this.resolveRiskFactorScore(dataObj, rf),
        isHeader: !!rf.isHeader,
        expandable,
        hideSubArrows: !!rf.hideSubArrows,
        path: rf.path,
        subFactors,
      };
    });
  }

  public isFactorClickable(rf: RiskFactor | null | undefined): boolean {
    if (!rf) return false;
    if (this.isCurrentMineInactive && this.isMineAssessment(rf)) {
      return false;
    }
    return !!rf.path || this.canExpandFactor(rf);
  }

  private isMineAssessment(rf: RiskFactor | null | undefined): boolean {
    return `${rf?.label || ''}`.trim().toLowerCase() === 'mine assessment';
  }

  private detectCurrentMineInactive(res: any): boolean {
    const previousNavState = (window?.history?.state || {}) as any;
    if (previousNavState?.selectedMine?.__isInactive === true) {
      return true;
    }

    const currentMineCandidates = [
      this.mineInfo?.name,
      this.getValueByPath(this.data, 'customPayload.mine'),
      previousNavState?.selectedMineName,
      previousNavState?.selectedMine?.name,
      previousNavState?.selectedMine?.__mineRaw
    ]
      .filter((value: any) => !!value)
      .map((value: any) => value.toString().toLowerCase().trim());

    if (currentMineCandidates.length === 0) {
      return false;
    }

    const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
    const candidateSet = new Set(currentMineCandidates.map((value) => normalize(value)));
    const responseList = Array.isArray(res?.responseList) ? res.responseList : [];

    const getAliasArray = (source: any, aliases: string[]): any[] => {
      if (!source || typeof source !== 'object') return [];

      for (const alias of aliases) {
        if (Array.isArray(source[alias])) {
          return source[alias];
        }
      }

      const aliasSet = new Set(aliases.map((alias) => normalize(alias)));
      for (const key of Object.keys(source)) {
        if (aliasSet.has(normalize(key)) && Array.isArray(source[key])) {
          return source[key];
        }
      }

      return [];
    };

    for (const node of responseList) {
      const inactiveGroups = getAliasArray(node, [
        'inactivemines',
        'inactiveMines',
        'inactiveRepowers',
        'inactive_repowers'
      ]);

      for (const group of inactiveGroups) {
        const rows = Array.isArray(group?.data) ? group.data : [];
        for (const row of rows) {
          const mineName = `${row?.mine || row?.name || ''}`.trim();
          if (mineName && candidateSet.has(normalize(mineName))) {
            return true;
          }
        }
      }
    }

    return false;
  }

  private getExplicitActiveRepowerFlag(): boolean | null {
    const previousNavState = (window?.history?.state || {}) as any;
    const selectedMine = previousNavState?.selectedMine;

    if (!selectedMine || typeof selectedMine !== 'object') {
      return null;
    }

    if (selectedMine.__isInactive === true) {
      return false;
    }

    if (selectedMine.__isActiveRepower === true) {
      return true;
    }

    // If the selection metadata exists but the point is not marked active, treat as non-active.
    if ('__isActiveRepower' in selectedMine || '__isInactive' in selectedMine) {
      return false;
    }

    return null;
  }

  private hasValidActiveRepowerData(dataObj: any): boolean {
    const rows = Array.isArray(dataObj?.repower_data) ? dataObj.repower_data : [];
    for (const row of rows) {
      const countRaw = row?.count_of_repowers;
      const countNumber = Number(countRaw);
      const hasCount = Number.isFinite(countNumber) ? countNumber > 0 : `${countRaw ?? ''}`.trim() !== '' && `${countRaw}`.trim() !== '0';

      if (hasCount) {
        return true;
      }
    }

    return false;
  }

  /* ---------- pre-fill labels before API responds ------------------- */
  private initRiskLabels(): void {
    const rfConfigs: any[] = this.data?.keyMappings?.riskFactors || [];
    this.riskFactors = rfConfigs.map((rf: any) => ({
      label: rf.label,
      score: 0,
      isHeader: !!rf.isHeader,
      expandable: !!rf.expandable,
      hideSubArrows: !!rf.hideSubArrows,
      path: rf.path,
    }));
  }

  private buildSubFactors(dataObj: any, rf: any): { label: string; score: number; path?: string; engineModelKey?: string; multiline?: boolean }[] {
    if (!rf?.subFactorsKey) return [];

    const raw = this.getValueByPath(dataObj, rf.subFactorsKey);
    if (raw === null || raw === undefined) return [];

    const result: { label: string; score: number; path?: string; engineModelKey?: string; multiline?: boolean }[] = [];
    const isActiveRepowers = `${rf?.label || ''}`.trim().toLowerCase() === 'active repowers';

    // Handle scalar values (like "High", "Neutral") for MTU and Customer fields
    if (!Array.isArray(raw)) {
      const scalarValue = String(raw).trim();
      if (scalarValue) {
        result.push({
          label: scalarValue,
          score: 0,
          path: rf.subFactorPath || rf.path,
        });
      }
      return result;
    }

    // Handle array values (like engine_model array)
    for (const item of raw) {
      if (isActiveRepowers) {
        const engineModelValue = `${item?.engine_model ?? item?.engine_model_key ?? item?.label ?? ''}`.trim();
        const countRaw = item?.count_of_repowers;
        const hasCount = countRaw !== null && countRaw !== undefined && `${countRaw}`.trim() !== '';
        const displayCount = hasCount ? String(countRaw).trim() : '0';
        const hasEngineModel = engineModelValue.length > 0;
        const label = hasEngineModel
          ? `Engine Model: ${engineModelValue}\nCount of Repower: ${displayCount}`
          : displayCount;
        const resolvedEngineModelKey = engineModelValue || undefined;

        result.push({
          label,
          score: 0,
          path: undefined,
          engineModelKey: resolvedEngineModelKey,
          multiline: hasEngineModel
        });
        if (!hasEngineModel) {
          console.log(this.nullEngineFlowDebugKey, 'buildSubFactors.nullEngine', {
            mine: this.mineInfo?.name || this.getValueByPath(this.data, 'customPayload.mine') || '',
            rawItem: item,
            builtSubFactor: {
              label,
              path: undefined,
              engineModelKey: resolvedEngineModelKey,
              multiline: hasEngineModel
            }
          });
        }
        continue;
      }

      const engineKey = `${item?.engine_model_key ?? item?.engine_model ?? item?.label ?? ''}`.trim();
      if (!engineKey) continue;

      result.push({
        label: engineKey,  // Display the engine model key as-is
        score: 0,
        path: rf.subFactorPath || rf.path,
        engineModelKey: engineKey  // Pass full key for API calls
      });
    }

    return result;
  }

  private resolveRiskFactorScore(dataObj: any, rf: any): number {
    const fallbackScore = this.overrideRepowerRiskForConfiguredMines(0, dataObj, rf);
    const scorePaths = Array.isArray(rf?.scoreKeys)
      ? rf.scoreKeys
      : rf?.scoreKey
        ? [rf.scoreKey]
        : [];

    for (const path of scorePaths) {
      const value = this.getValueByPath(dataObj, path);
      const numericValue = Number(value);
      if (Number.isFinite(numericValue) && numericValue > 0) {
        return this.overrideRepowerRiskForConfiguredMines(numericValue, dataObj, rf);
      }
    }

    return fallbackScore;
  }

  private resolveHeaderValue(label: string, path: string, dataObj: any): string {
    const normalizedLabel = (label || '').toLowerCase().trim();
    if (normalizedLabel === 'engine') {
      const engineKeys = this.getEngineModelKeys(dataObj);
      if (engineKeys.length > 0) {
        return engineKeys.map((key) => this.formatEngineDisplayLabel(key)).join(' / ');
      }
    }
    if (normalizedLabel === 'fleet') {
      const fleets = this.getFleetValues(dataObj);
      if (fleets.length > 0) {
        return fleets.join(' / ');
      }
    }

    return String(this.getValueByPath(dataObj, path) ?? '');
  }

  private getEngineModelKeys(dataObj: any): string[] {
    const raw = dataObj?.engine_model;
    const engineModels = Array.isArray(raw) ? raw : [];

    const keys = engineModels
      .map((item: any) =>
        `${item?.engine_model_key ?? item?.engine_model ?? item?.engine_model_without_emission ?? ''}`.trim()
      )
      .filter((value: string) => value.length > 0);

    return Array.from(new Set(keys));
  }

  private extractPrimaryEngineModelKey(dataObj: any): string {
    const keys = this.getEngineModelKeys(dataObj);
    if (keys.length > 0) {
      return keys[0];
    }

    return String(this.getValueByPath(dataObj, 'engine_model.0.engine_model_key') ?? '').trim();
  }

  private getFleetValues(dataObj: any): string[] {
    const raw = dataObj?.engine_model;
    const engineModels = Array.isArray(raw) ? raw : [];

    const fleets = engineModels
      .map((item: any) => `${item?.fleet ?? ''}`.trim())
      .filter((value: string) => value.length > 0);

    return Array.from(new Set(fleets));
  }

  private formatEngineDisplayLabel(value: string): string {
    return String(value || '')
      .split('/')
      .map((part) => part.trim().replace(/,/g, '-'))
      .filter((part) => part.length > 0)
      .join(' / ');
  }

  private resolveMineNoteText(km: any, dataObj: any): string {
    const candidatePaths: string[] = [];

    if (km?.mineNoteKey) {
      candidatePaths.push(String(km.mineNoteKey));
    }

    // Fallbacks for backward compatibility across API payload variants.
    candidatePaths.push(
      'owners.0.mine_background',
      'owners.0.mineBackground',
      'mine_background',
      'mineBackground',
      'owners.0.background'
    );

    for (const path of candidatePaths) {
      const value = this.getValueByPath(dataObj, path);
      const text = String(value ?? '').trim();
      if (text) return text;
    }

    return '';
  }

  /* ---------- helpers ----------------------------------------------- */
  private getValueByPath(obj: any, path: string): any {
    if (!obj || !path) return undefined;
    let cur = obj;
    for (const p of String(path).split('.')) {
      if (cur == null) return undefined;
      cur = cur[p];
    }
    return cur;
  }

  toggleFactor(label: string): void {
    this.expandedFactors.has(label)
      ? this.expandedFactors.delete(label)
      : this.expandedFactors.add(label);
  }

  isExpanded(label: string): boolean {
    return this.expandedFactors.has(label);
  }

  canExpandFactor(rf: RiskFactor): boolean {
    if (this.isMineRiskLevel(rf)) {
      return false;
    }

    return !!rf?.expandable && Array.isArray(rf?.subFactors) && rf.subFactors.length > 0;
  }

  toggleCatastrophic(): void {
    this.catastrophicFeature.isExpanded = !this.catastrophicFeature.isExpanded;
  }

  onCatastrophicEngineClick(engItem: { raw: string, display: string }): void {
    const mineName = (this.mineInfo?.name || this.getValueByPath(this.data, 'customPayload.mine') || '').toString().trim();

    // New JSON structure for API, completely independent
    const apiPayload = {
      functionName: 'FailureDetails',
      requestData: {
        mineName: mineName,
        engineModel: engItem.raw
      }
    };

    // Trigger FailureDetails API
    if (this.data?.apiUrl) {
      this.configService.getConfig(this.data.apiUrl, apiPayload, {}, 'post').subscribe({
        next: (res) => console.log('FailureDetails response:', res),
        error: (err) => console.error('FailureDetails error:', err)
      });
    }

    // Retain existing routing/flow UI pattern
    const ownerValue = this.getMineDetailValue('Owner');
    const previousState = (window?.history?.state || {}) as any;
    const path = '/catastrophic-failure';

    this.navigateToPath(
      path,
      this.buildNavigationState(
        mineName,
        engItem.raw,
        engItem.raw,
        ownerValue,
        previousState
      )
    );
  }

  shouldShowSubFactors(rf: RiskFactor): boolean {
    const result = !rf.isHeader && this.canExpandFactor(rf) && this.isExpanded(rf.label);
    const isActiveRepowers = `${rf?.label || ''}`.trim().toLowerCase().startsWith('active repowers');
    const hasNullEngineSubFactor = isActiveRepowers && Array.isArray(rf?.subFactors)
      ? rf.subFactors.some((sub: any) => !`${sub?.engineModelKey ?? ''}`.trim())
      : false;

    if (hasNullEngineSubFactor) {
      console.log(this.nullEngineFlowDebugKey, 'shouldShowSubFactors', {
        label: rf.label,
        canExpand: this.canExpandFactor(rf),
        isExpanded: this.isExpanded(rf.label),
        result,
        subFactors: rf.subFactors
      });
    }

    return result;
  }

  onSubFactorClick(parent: RiskFactor, sub: { label: string; path?: string; engineModelKey?: string }): void {
    const isActiveRepowers = `${parent?.label || ''}`.trim().toLowerCase().startsWith('active repowers');
    const isNullEngineSubFactor = isActiveRepowers && !`${sub?.engineModelKey ?? ''}`.trim();
    if (!sub?.path) {
      if (isNullEngineSubFactor) {
        console.log(this.nullEngineFlowDebugKey, 'onSubFactorClick.noPath', {
          parentLabel: parent?.label || '',
          sub,
          mine: this.mineInfo?.name || this.getValueByPath(this.data, 'customPayload.mine') || ''
        });
      }
      return;
    }

    const mine = (this.mineInfo?.name || this.getValueByPath(this.data, 'customPayload.mine') || '').toString().trim();
    const engineFromHeader = this.getMineDetailValue('Engine');
    const engineModelKey = (sub.engineModelKey || sub.label || engineFromHeader || '').toString().trim();
    const selectedEngine = (sub.label || '').toString().trim();
    const ownerValue = this.getMineDetailValue('Owner');
    const previousState = (window?.history?.state || {}) as any;

    this.navigateToPath(
      sub.path,
      this.buildNavigationState(
        mine,
        engineModelKey,
        selectedEngine || engineModelKey,
        ownerValue,
        previousState
      )
    );
  }

  private buildNavigationState(
    mineName: string,
    engineModelKey: string,
    selectedEngine: string,
    ownerValue: string,
    previousState: any
  ): any {
    const prevMine = previousState?.selectedMine && typeof previousState.selectedMine === 'object'
      ? previousState.selectedMine
      : {};

    return {
      ...previousState,
      treeDrillNavigationAt: Date.now(),
      expandedFactors: Array.from(this.expandedFactors || []),
      catastrophicExpanded: this.catastrophicFeature.isExpanded,
      selectedMineName: mineName,
      mine_site: mineName,
      selectedMine: {
        ...prevMine,
        name: mineName,
      },
      selectedCustomer: ownerValue || previousState?.selectedCustomer || '',
      selectedEngineModelKey: engineModelKey,
      selectedEngineWithoutEmission: selectedEngine || engineModelKey,
      selectedEngine: engineModelKey,
      engine_model: engineModelKey,
      engine_model_key: engineModelKey,
      engine_model_without_emission: selectedEngine || engineModelKey,
      selectedTruckModel: this.resolveCleanEquipmentModel(engineModelKey),
    };
  }

  private resolveCleanEquipmentModel(engineModelKey: string): string {
    const normalizedKey = (engineModelKey || '').trim().toLowerCase();

    // Try to find a matching engine_model item by key
    const match = this.engineModelItems.find((item: any) => {
      const key = `${item?.engine_model_key ?? item?.engine_model ?? ''}`.trim().toLowerCase();
      return key === normalizedKey;
    });

    // Prefer direct fields, then parse from fleet string
    const item = match || this.engineModelItems[0];
    if (!item) return '';

    const direct = `${item?.clean_equipment_model ?? item?.truck_model ?? ''}`.trim();
    if (direct) return direct;

    // Parse equipment model from fleet string like "56x 930e-4, LF 38%"
    const fleet = `${item?.fleet ?? ''}`.trim();
    const fleetMatch = fleet.match(/\d+x\s+([^,]+)/i);
    return fleetMatch ? fleetMatch[1].trim() : '';
  }

  private resolveEngineContextForNavigation(
    previousState: any,
    fallbackEngineModelKey: string
  ): { engineModelKey: string; selectedEngineLabel: string } {
    const stateEngineModelKey = String(
      previousState?.selectedEngineModelKey ||
      previousState?.engine_model_key ||
      previousState?.selectedEngine ||
      previousState?.engine_model ||
      ''
    ).trim();

    const engineModelKey = stateEngineModelKey || String(fallbackEngineModelKey || '').trim();
    const stateEngineLabel = String(
      previousState?.selectedEngineWithoutEmission ||
      previousState?.engine_model_without_emission ||
      ''
    ).trim();

    return {
      engineModelKey,
      selectedEngineLabel: stateEngineLabel || engineModelKey,
    };
  }

  private navigateToPath(path: string, state: any): void {
    if (!path) return;

    const target = this.normalizePath(path);
    const current = this.normalizePath(this.router.url || '');

    if (target === current) {
      this.router.navigate([target], {
        fragment: `tree-drill-${Date.now()}`,
        replaceUrl: true,
        state,
      });
      return;
    }

    this.router.navigate([target], { state });
  }

  private normalizePath(path: string): string {
    const value = String(path || '').trim();
    if (!value) return '';
    const withoutQuery = value.split('?')[0].split('#')[0].trim();
    const withLeadingSlash = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
    return withLeadingSlash.replace(/\/+$/, '') || '/';
  }

  private getMineDetailValue(label: string): string {
    const target = (label || '').toLowerCase().trim();
    const detail = (this.mineInfo?.details || []).find(
      (d) => (d?.label || '').toLowerCase().trim() === target
    );
    return (detail?.value || '').toString().trim();
  }

  getScoreClass(score: number): string {
    const value = Number(score);
    // Treat scores that are 0 or less than 1 as "none" (grey) - including displayed "0.0" values
    if (!Number.isFinite(value) || value < 1) {
      return 'score-none';
    }

    // Keep tree drill colors aligned with the 3-band Repower Risk legend.
    if (value >= 5) {
      return 'score-high';
    }
    if (value > 3) {
      return 'score-medium';
    }

    return 'score-low';
  }

  getDisplayScore(rf: RiskFactor | any): string {
    const value = Number(rf?.score || 0);
    if (!Number.isFinite(value)) {
      return '0';
    }

    const singleDecimalWithoutRounding = Math.trunc(value * 10) / 10;
    return singleDecimalWithoutRounding.toFixed(1);
  }

  showScoreBadge(rf: RiskFactor | any): boolean {
    const label = `${rf?.label || ''}`.trim().toLowerCase();
    const isActiveRepowersLabel = label.startsWith('active repowers');
    return label !== 'fleet life stage' && !isActiveRepowersLabel;
  }

  private isMineRiskLevel(rf: RiskFactor | null | undefined): boolean {
    return `${rf?.label || ''}`.trim().toLowerCase() === 'mine risk level';
  }

  private overrideRepowerRiskForConfiguredMines(score: number, dataObj: any, rf: any): number {
    const label = `${rf?.label || ''}`.trim().toLowerCase();
    if (label !== 'repower risk level') {
    return score;
  }

//   const mineCandidates = [
//   this.mineInfo?.name,
//   this.getValueByPath(this.data, 'customPayload.mine'),
//   this.getMineDetailValue('mine'),
//   this.getMineDetailValue('mine_name'),
//   this.getMineDetailValue('mine_site'),
//   this.getMineDetailValue('name'),
//   ]

//   const isTargetMine = mineCandidates.some((candidate) => {
//   const mineName = String(candidate || '').trim().toLowerCase();
//   return mineName.includes('lumwana') || mineName.includes('sishen') || mineName.includes('centinela') || mineName.includes('los pelambres') || mineName.includes('fort hills') || mineName.includes('marigold') || mineName.includes('los Pelambres');
// });

return score;
}
}
